import type { BaseVerificationViewModel } from '@lib/store/onboarding/BaseVerificationViewModel'
import { reaction } from 'mobx'
import { type RefObject, useEffect } from 'react'

/**
 * Hook for managing input focus in verification components.
 * Automatically focuses the appropriate input based on the current verification step.
 *
 * @param viewModel - The verification view model instance
 * @param inputRef - Ref to the main input field (email/phone)
 * @param codeInputRef - Ref to the verification code input field
 */
export function useVerificationFocus(
    viewModel: BaseVerificationViewModel,
    inputRef: RefObject<HTMLInputElement | null>,
    codeInputRef: RefObject<HTMLInputElement | null>,
) {
    useEffect(() => {
        const focusInput = (step: string) => {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                if (step === 'input' && inputRef.current) {
                    inputRef.current.focus()
                } else if (step === 'code-input' && codeInputRef.current) {
                    codeInputRef.current.focus()
                }
            })
        }

        // Focus on initial mount
        focusInput(viewModel.currentStep)

        // React to step changes using MobX reaction
        const dispose = reaction(
            () => viewModel.currentStep,
            (step) => focusInput(step),
        )

        return dispose
    }, [viewModel, inputRef, codeInputRef])
}
